// Detecção de áudio vazio no FRONT: evita mandar WAV de silêncio/ruído ao backend
// (que custaria uma chamada de STT + Gemini para responder nada).

/** RMS mínimo de uma janela para contar como "som" (escala 0..1 do PCM). */
const LIMIAR_RMS = 0.01;
/** Tamanho da janela de análise. */
const JANELA_MS = 20;
/** Som acumulado mínimo para o áudio valer um envio. Abaixo disso é estalo/ruído. */
const MIN_SOM_MS = 250;

/**
 * Julga se um WAV (PCM 16-bit mono, como o produzido por `float32ToWav16k`) está vazio:
 * sem janelas de energia acima do piso, ou com menos de {@link MIN_SOM_MS} de som acumulado.
 *
 * Curto-circuita no `false` assim que o mínimo de som é atingido — não varre o resto do buffer.
 */
export async function audioVazio(wav: Blob): Promise<boolean> {
  if (wav.size <= 44) return true;

  const buf = await wav.arrayBuffer();
  const view = new DataView(buf);
  // Taxa lida do próprio cabeçalho (offset 24) — nunca assumida.
  const taxa = view.getUint32(24, true) || 16000;
  const nAmostras = Math.floor((buf.byteLength - 44) / 2);
  const porJanela = Math.max(1, Math.round((JANELA_MS / 1000) * taxa));

  let msComSom = 0;
  for (let ini = 0; ini < nAmostras; ini += porJanela) {
    const fim = Math.min(nAmostras, ini + porJanela);
    let soma = 0;
    for (let i = ini; i < fim; i++) {
      const s = view.getInt16(44 + i * 2, true) / 0x8000;
      soma += s * s;
    }
    const rms = Math.sqrt(soma / (fim - ini));
    if (rms > LIMIAR_RMS) {
      msComSom += JANELA_MS;
      if (msComSom >= MIN_SOM_MS) return false;
    }
  }
  return true;
}
